import { Box, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';

import Search from 'components/Common/Search';
import { MdOutlineCalendarToday, MdOutlineMoreVert, MdOutlineTextSnippet } from 'react-icons/md';
import Button from 'components/Common/Button';
import { GoPlus } from 'react-icons/go';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import NoResult from 'assets/NoResult.svg';
import {
  useCloneKnowledgeBase,
  useDeleteKnowledgeBase,
  useGetKnowledgeBases
} from 'apis/business/setting/knowledgeBase/hook';
import Spinner from 'components/Common/Spinner';
import Grid from '@mui/material/Grid2';
import { formatDate } from 'utils/helperFunctions';

export default function KnowledgeBases() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    id: number | null;
  }>({
    anchorEl: null,
    id: null
  });

  const [searchText, setSearchText] = useState<null | string>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setMenuState({ anchorEl: event.currentTarget, id });
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, id: null });
  };

  const { data: knowledgeBases, isLoading } = useGetKnowledgeBases(searchText);
  const { mutate: cloneKnowledgeBase } = useCloneKnowledgeBase();
  const { mutate: deleteKnowledgeBase } = useDeleteKnowledgeBase();
  const isKnowledgeBaseEmpty = !Boolean(knowledgeBases?.data.length);

  const handleDelete = (id: number) => {
    deleteKnowledgeBase(String(id));
    handleClose();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant='h6'>Knowledge Base</Typography>
        <Box display='flex' gap={3}>
          <Search
            size='small'
            sx={{ background: theme.palette.background.paper }}
            placeholder='Search by name'
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <Button onClick={() => navigate('add')} variant='contained' color='primary' startIcon={<GoPlus />}>
            Create Knowledge Base
          </Button>
        </Box>
      </Box>
      {isKnowledgeBaseEmpty ? (
        <EmptyStateView />
      ) : (
        <Grid container spacing={3} mt={3}>
          {knowledgeBases?.data.map((item, index) => (
            <Grid
              key={index}
              size={{ xs: 12, md: 6, lg: 4 }}
              bgcolor={theme.palette.background.paper}
              border='1px solid #F3F3F3'
              borderRadius={0.5}
              padding={3}
            >
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Link to={String(item.id)}>
                  <Typography variant='h3' fontSize={18} fontWeight={600} color='primary'>
                    {item.name}
                  </Typography>
                </Link>
                <IconButton
                  id='basic-button'
                  aria-controls={menuState.id === item.id ? 'basic-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={menuState.id === item.id ? 'true' : undefined}
                  onClick={(event) => handleClick(event, item.id)}
                >
                  <MdOutlineMoreVert />
                </IconButton>
                <Menu
                  id={`basic-menu-${item.id}`}
                  anchorEl={menuState.anchorEl}
                  open={menuState.id === item.id}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button'
                  }}
                  slotProps={{ paper: { sx: { width: 120 } } }}
                >
                  <Link to={String(item.id)}>
                    <MenuItem onClick={handleClose}>Edit</MenuItem>
                  </Link>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      cloneKnowledgeBase(item.id);
                    }}
                  >
                    Dulplicate
                  </MenuItem>
                  <Link to={`${item.id}/edit`}>
                    <MenuItem onClick={handleClose}>Rename</MenuItem>
                  </Link>
                  <MenuItem onClick={() => handleDelete(item.id)}>Delete</MenuItem>
                </Menu>
              </Box>
              <Typography color='secondary' variant='body2' marginBottom={3} marginTop={1}>
                {item.description}
              </Typography>
              <Box color={theme.palette.secondary.main} display='flex' gap={1} alignItems='center'>
                <MdOutlineTextSnippet />
                <Typography variant='body2' color='#000'>
                  {item.datasets.length} Docs
                </Typography>
              </Box>
              <Box display='flex' gap={1} alignItems='center' marginTop={1}>
                <MdOutlineCalendarToday />
                <Typography variant='body2'>{formatDate(item.created_at)}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      <Outlet />
    </>
  );
}

const EmptyStateView = () => {
  return (
    <Box display='flex' flexDirection='column' alignItems='center' marginY={14}>
      <img src={NoResult} alt='No data found' />
      <Typography color='secondary' marginTop={2}>
        No Data Found
      </Typography>
    </Box>
  );
};
